import React from 'react';
import { LucideIcon, Info } from 'lucide-react';

interface BreakdownItem {
    name: string;
    value: string;
}

interface StatsCardProps {
    label: string;
    value: string;
    change?: string;
    icon: LucideIcon;
    color?: 'primary' | 'success' | 'error' | 'warning' | 'info';
    breakdown?: BreakdownItem[];
}

export default function StatsCard({ label, value, change, icon: Icon, color = 'primary', breakdown }: StatsCardProps) {
    const isPositive = change?.startsWith('+');

    const colorClasses = {
        primary: 'bg-primary-soft text-primary border-primary/10',
        success: 'bg-success-soft text-success border-success/10',
        error: 'bg-error-soft text-error border-error/10',
        warning: 'bg-warning-soft text-warning border-warning/10',
        info: 'bg-info-soft text-info border-info/10',
    };

    const iconColorClasses = {
        primary: 'text-primary',
        success: 'text-success',
        error: 'text-error',
        warning: 'text-warning',
        info: 'text-info',
    };

    return (
        <div className="group relative p-8 rounded-[2.5rem] bg-surface border border-border-main transition-all duration-300 hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-1 overflow-visible hover:z-50">
            <div className="flex justify-between items-start mb-6">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 ${colorClasses[color]}`}>
                    <Icon className="w-7 h-7" />
                </div>
                {change && (
                    <span className={`text-[11px] font-bold px-3 py-1.5 rounded-full backdrop-blur-sm ${isPositive ? 'bg-success-soft/80 text-success' : 'bg-error-soft/80 text-error'
                        }`}>
                        {change}
                    </span>
                )}
            </div>

            <div className="space-y-1">
                <p className="text-[11px] font-semibold text-muted uppercase tracking-[0.15em]">{label}</p>
                <div className="flex items-baseline gap-2 overflow-hidden">
                    <h3 className="text-3xl font-semibold text-foreground tracking-tight whitespace-nowrap">{value}</h3>
                </div>
            </div>

            {breakdown && breakdown.length > 0 && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-[105%] w-[280px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0 z-50">
                    <div className="bg-surface/90 backdrop-blur-xl border border-border-main p-6 rounded-3xl shadow-2xl relative">
                        <div className="flex items-center gap-2 mb-4 border-b border-border-main/50 pb-3">
                            <Info className={`w-4 h-4 ${iconColorClasses[color]}`} />
                            <p className="text-[10px] font-bold text-foreground uppercase tracking-widest">Detail Per Unit</p>
                        </div>

                        <div className="space-y-3">
                            {breakdown.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-center group/item">
                                    <span className="text-[11px] text-secondary font-medium group-hover/item:text-foreground transition-colors">{item.name}</span>
                                    <span className="text-[11px] font-bold text-foreground tabular-nums">{item.value}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 pt-3 border-t border-border-main/50 flex justify-between items-center">
                            <span className="text-[10px] font-black text-muted uppercase">Total Agregasi</span>
                            <span className={`text-[11px] font-black ${iconColorClasses[color]}`}>{value}</span>
                        </div>

                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-surface/90 border-r border-b border-border-main rotate-45"></div>
                    </div>
                </div>
            )}
        </div>
    );
}
